import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { authApi } from '../../lib/apiClient'
import { Mail, Lock, User, Handshake } from 'lucide-react'
import { toast } from '../../lib/toast'
import styles from './Register.module.css'
import SecureInput from '../../ui/SecureInput/SecureInput'
import { validateEmail, validatePassword, validateName } from '../../utils/security'
import { useAccountBlock } from '../../hooks/useAccountBlock'

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  })
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { timeRemaining, isBlocked, handleError, clearBlock, checkBeforeSubmit } = useAccountBlock()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Check if account is still blocked
    if (!checkBeforeSubmit()) {
      return
    }

    // Validate all fields before submission
    const emailValidation = validateEmail(formData.email)
    const passwordValidation = validatePassword(formData.password)
    const confirmPasswordValid = formData.password === formData.confirmPassword && validatePassword(formData.confirmPassword).valid
    const firstNameValidation = validateName(formData.first_name, 'First name')
    const lastNameValidation = validateName(formData.last_name, 'Last name')

    // Check if all validations passed
    if (!emailValidation.valid || !passwordValidation.valid || !confirmPasswordValid ||
      !firstNameValidation.valid || !lastNameValidation.valid) {
      toast.error('Please fix all validation errors before submitting')
      return
    }

    setLoading(true)

    try {
      await authApi.register({
        email: emailValidation.sanitized,
        password: passwordValidation.sanitized,
        first_name: firstNameValidation.sanitized,
        last_name: lastNameValidation.sanitized,
      })
      // Clear blocked state on successful registration
      clearBlock()
      toast.success('Account created successfully! Your account is now pending approval.')
      navigate('/pending')
    } catch (err: any) {
      // Extract error message from server response
      // Server provides detailed error messages that should be displayed to the user
      const errorMessage = err.message || 'Registration failed. Please try again.'
      
      // Handle block errors
      handleError(err)
      
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div className={styles.logo}>
            <Handshake size={32} />
          </div>
          <h1>Create Account</h1>
          <p>Get started with Oitools Studio</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <SecureInput
            fieldType="name"
            fieldName="First name"
            label="First Name"
            icon={<User size={16} />}
            value={formData.first_name}
            onChange={(value) => setFormData(prev => ({ ...prev, first_name: value }))}
            placeholder="John"
            disabled={loading}
            required
            name="first_name"
            autoComplete="given-name"
            className={styles.input}
          />

          <SecureInput
            fieldType="name"
            fieldName="Last name"
            label="Last Name"
            icon={<User size={16} />}
            value={formData.last_name}
            onChange={(value) => setFormData(prev => ({ ...prev, last_name: value }))}
            placeholder="Doe"
            disabled={loading}
            required
            name="last_name"
            autoComplete="family-name"
            className={styles.input}
          />

          <SecureInput
            fieldType="email"
            label="Email Address"
            icon={<Mail size={16} />}
            value={formData.email}
            onChange={(value) => setFormData(prev => ({ ...prev, email: value }))}
            placeholder="john@company.com"
            disabled={loading}
            required
            name="email"
            autoComplete="email"
            className={styles.input}
          />

          <SecureInput
            fieldType="password"
            label="Password"
            icon={<Lock size={16} />}
            value={formData.password}
            onChange={(value) => setFormData(prev => ({ ...prev, password: value }))}
            placeholder="Min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special"
            disabled={loading}
            required
            name="password"
            autoComplete="new-password"
            className={styles.input}
          />

          <SecureInput
            fieldType="confirmPassword"
            label="Confirm Password"
            icon={<Lock size={16} />}
            value={formData.confirmPassword}
            onChange={(value) => setFormData(prev => ({ ...prev, confirmPassword: value }))}
            matchValue={formData.password}
            placeholder="Type your password again"
            disabled={loading}
            required
            className={styles.input}
            preventPaste
            name="confirmPassword"
            autoComplete="new-password"
          />
          <button
            type="submit"
            disabled={loading || isBlocked}
            className={styles.button}
          >
            {isBlocked 
              ? `Try again in ${timeRemaining}` 
              : loading 
                ? 'Creating Account...' 
                : 'Create Account'}
          </button>
        </form>

        <div className={styles.footer}>
          <p>
            Already have an account?{' '}
            <Link to="/login" className={styles.link}>
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}